import type { FormEvent } from 'react';
import { Controller, type Control } from 'react-hook-form';
import { Alert, Button, Input, Label } from '@shared/components';
import type { ThirdPartyInvoiceNotice, ThirdPartyInvoiceSubmit } from '../../hooks/useThirdPartyInvoiceSubmit';
import type { ThirdPartyInvoiceValues } from '../../schemas/thirdPartyInvoice.schema';
import {
    THIRD_PARTY_INVOICE_REJECTED,
    THIRD_PARTY_INVOICE_UNCONFIRMED,
    THIRD_PARTY_INVOICING_NOT_FOUND,
    thirdPartyInvoiceSubmitted,
} from '../../utils/thirdPartyInvoicingCopy';

interface Props {
    invoice: ThirdPartyInvoiceSubmit;
}

interface TextFieldProps {
    id: string;
    name: keyof ThirdPartyInvoiceValues;
    label: string;
    control: Control<ThirdPartyInvoiceValues>;
    numeric?: boolean;
}

const DISABLED_REASON_ID = 'third-party-invoice-disabled-reason';

function TextField({ id, name, label, control, numeric = false }: TextFieldProps) {
    const errorId = `${id}-error`;

    return (
        <div>
            <Label htmlFor={id} required>
                {label}
            </Label>
            <Controller
                name={name}
                control={control}
                render={({ field, fieldState: { error } }) => (
                    <Input
                        id={id}
                        ref={field.ref}
                        type="text"
                        inputMode={numeric ? 'numeric' : undefined}
                        autoComplete="off"
                        value={field.value}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        error={!!error}
                        hint={error?.message}
                        hintId={errorId}
                        aria-invalid={!!error}
                        aria-describedby={error ? errorId : undefined}
                    />
                )}
            />
        </div>
    );
}

export function ThirdPartyInvoiceNoticeAlert({ notice }: { notice: ThirdPartyInvoiceNotice | null }) {
    switch (notice?.kind) {
        case 'submitted':
            return (
                <Alert variant="success" role="status">
                    {thirdPartyInvoiceSubmitted(notice.invoiceNumber)}
                </Alert>
            );
        case 'rejected':
            return <Alert variant="error">{THIRD_PARTY_INVOICE_REJECTED}</Alert>;
        case 'unconfirmed':
            return <Alert variant="warning">{THIRD_PARTY_INVOICE_UNCONFIRMED}</Alert>;
        case 'not-found':
            return <Alert variant="error">{THIRD_PARTY_INVOICING_NOT_FOUND}</Alert>;
        default:
            return null;
    }
}

export function ThirdPartyInvoiceForm({ invoice }: Props) {
    const { control, submit, isSubmitting, disabledReason, notice } = invoice;
    const submitDisabled = disabledReason !== null || isSubmitting;

    const guardedSubmit = (event: FormEvent<HTMLFormElement>) => {
        if (submitDisabled) {
            event.preventDefault();
            return;
        }
        void submit(event);
    };

    return (
        <form onSubmit={guardedSubmit} className="space-y-4" noValidate>
            <TextField id="third-party-invoice-number" name="invoiceNumber" label="Invoice number" control={control} />
            <TextField id="third-party-invoice-mileage" name="mileage" label="Mileage" control={control} numeric />
            <ThirdPartyInvoiceNoticeAlert notice={notice} />
            <div>
                <Button
                    type="submit"
                    size="sm"
                    variant="primary"
                    appearance="solid"
                    className="w-full"
                    ariaDisabled={submitDisabled}
                    ariaDescribedBy={disabledReason ? DISABLED_REASON_ID : undefined}
                >
                    Submit invoice
                </Button>
                {disabledReason && (
                    <p id={DISABLED_REASON_ID} className="mt-1.5 text-xs text-mp-muted">
                        {disabledReason}
                    </p>
                )}
            </div>
        </form>
    );
}
